/**
 * A tiny, framework-free slide carousel state machine.
 *
 * The navigation/clamping logic lives here (pure, unit-testable in Node with no
 * DOM); how a step is reflected on screen is supplied by the caller's `render`.
 * Both games' learn-slides reveal is built on top of this via `createLearnCarousel`.
 */

/** Clamp a slide index into `[0, count - 1]`. */
export const clampIndex = (i: number, count: number): number =>
  Math.max(0, Math.min(count - 1, i));

export interface CarouselView {
  /** Total number of slides. */
  count: number;
  /** Reflect the active slide on screen (toggle classes, disable nav, …). */
  render(index: number): void;
}

export interface Carousel {
  /** The currently active slide index. */
  readonly index: number;
  /** Jump to slide `i` (clamped) and render. */
  goto(i: number): void;
  /** Move by `delta` slides (clamped) and render. */
  step(delta: number): void;
  /** Return to the first slide and render. */
  reset(): void;
}

export function createCarousel(view: CarouselView): Carousel {
  let idx = 0;
  const goto = (i: number): void => {
    idx = clampIndex(i, view.count);
    view.render(idx);
  };
  return {
    get index(): number {
      return idx;
    },
    goto,
    step: (delta: number): void => goto(idx + delta),
    reset: (): void => goto(0),
  };
}
